import React, { memo } from 'react'

import {
  AdminPanelSettings,
  ContactSupport,
  Download,
  EventNote,
  Fingerprint,
  Forum,
  MenuBook,
  NavigateNext,
  School,
  Security,
  SupportAgent,
  VideoLibrary,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  Paper,
  Rating,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'

interface TrainingResourcesProps {
  mounted: boolean
}

const trainingPrograms = [
  {
    title: 'Biometric Enrollment Certification',
    category: 'Technical',
    duration: '3 weeks',
    enrolled: 1234,
    rating: 4.8,
    status: 'enrolling',
    icon: <Fingerprint />,
    color: '#3B82F6',
  },
  {
    title: 'Data Privacy & Security',
    category: 'Compliance',
    duration: '2 weeks',
    enrolled: 2156,
    rating: 4.9,
    status: 'popular',
    icon: <Security />,
    color: '#059669',
  },
  {
    title: 'Customer Service Excellence',
    category: 'Soft Skills',
    duration: '1 week',
    enrolled: 3421,
    rating: 4.7,
    status: 'enrolling',
    icon: <SupportAgent />,
    color: '#F59E0B',
  },
  {
    title: 'System Administration',
    category: 'Technical',
    duration: '4 weeks',
    enrolled: 567,
    rating: 4.6,
    status: 'advanced',
    icon: <AdminPanelSettings />,
    color: '#DC2626',
  },
]

const quickResources = [
  { icon: <MenuBook />, title: 'User Manuals', count: '127 docs', color: '#3B82F6' },
  { icon: <VideoLibrary />, title: 'Video Tutorials', count: '84 videos', color: '#059669' },
  { icon: <Download />, title: 'Forms & Templates', count: '45 files', color: '#F59E0B' },
  { icon: <Forum />, title: 'Discussion Forum', count: '2.3k topics', color: '#8B5CF6' },
  { icon: <ContactSupport />, title: 'FAQs', count: '156 items', color: '#DC2626' },
  { icon: <EventNote />, title: 'Event Calendar', count: '12 upcoming', color: '#64748B' },
]

const TrainingResources = memo<TrainingResourcesProps>(({ mounted: _mounted }) => {
  const theme = useTheme()

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'popular':
        return <Chip label="Popular" size="small" color="success" />
      case 'advanced':
        return <Chip label="Advanced" size="small" color="error" />
      default:
        return null
    }
  }

  return (
    <Box sx={{ py: 8, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
      <Container maxWidth="xl">
        <Box textAlign="center" mb={6}>
          <Chip
            icon={<School />}
            label="TRAINING & RESOURCES"
            color="success"
            variant="outlined"
            sx={{ mb: 3, fontWeight: 600 }}
          />
          <Typography variant="h3" fontWeight={800} gutterBottom>
            Professional Development
          </Typography>
          <Typography variant="h6" color="text.secondary" maxWidth={800} mx="auto">
            Enhance your skills with our comprehensive training programs and resources
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Training Programs */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography variant="h5" fontWeight={700} mb={3}>
              Featured Training Programs
            </Typography>
            <Grid container spacing={3}>
              {trainingPrograms.map((program, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6 }}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                        borderColor: alpha(program.color, 0.3),
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1.5,
                            bgcolor: alpha(program.color, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: program.color,
                          }}
                        >
                          {program.icon}
                        </Box>
                        {getStatusChip(program.status)}
                      </Stack>

                      <Box>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                          {program.title}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Chip label={program.category} size="small" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            {program.duration}
                          </Typography>
                        </Stack>
                      </Box>

                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Rating value={program.rating} precision={0.1} size="small" readOnly />
                          <Typography variant="caption" color="text.secondary">
                            ({program.rating})
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {program.enrolled.toLocaleString()} enrolled
                        </Typography>
                      </Stack>

                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{
                          fontWeight: 600,
                          borderWidth: 1.5,
                          '&:hover': {
                            borderWidth: 1.5,
                            borderColor: program.color,
                            bgcolor: alpha(program.color, 0.05),
                          },
                        }}
                      >
                        Enroll Now
                      </Button>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Quick Resources */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="h5" fontWeight={700} mb={3}>
              Quick Resources
            </Typography>
            <Stack spacing={2}>
              {quickResources.map((resource, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: alpha(resource.color, 0.05),
                      borderColor: alpha(resource.color, 0.3),
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: alpha(resource.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: resource.color,
                      }}
                    >
                      {resource.icon}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {resource.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {resource.count}
                      </Typography>
                    </Box>
                    <NavigateNext fontSize="small" color="action" />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
})

TrainingResources.displayName = 'TrainingResources'

export default TrainingResources
